import { type FC, useState, useEffect } from 'react'
import cn from 'classnames'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { twMerge } from 'tailwind-merge'
import { Square2StackIcon } from '@heroicons/react/24/outline'
import { PencilIcon } from '@heroicons/react/24/solid'
import { useMountedState } from 'react-use'
import { type DefaultProps } from '../common'

export interface FieldProps extends DefaultProps {
  label?: string
  value: string
  rightValue?: string | React.ReactNode
  canCopy?: boolean
  onEdit?: () => void
  copiedMessage?: string
  valueClassName?: string
  isPII?: boolean
}

export const Field: FC<FieldProps> = ({
  label,
  value,
  rightValue,
  canCopy,
  onEdit,
  copiedMessage = 'Copied',
  className,
  id,
  testId,
  ariaLabel,
  valueClassName,
  isPII,
}) => {
  const isMounted = useMountedState()
  const [copied, setCopied] = useState(false)
  const [fadeOut, setFadeOut] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let timeoutId: NodeJS.Timeout
    // eslint-disable-next-line no-undef
    let fadeOutTimeoutId: NodeJS.Timeout

    if (copied) {
      // Set fade-out timeout to trigger 1500ms after the "Copied" text appears.
      fadeOutTimeoutId = setTimeout(() => {
        if (isMounted()) {
          setFadeOut(true)
        }
      }, 1500)

      // Set the final timeout to hide the "Copied" text completely after 500ms of fading out
      timeoutId = setTimeout(() => {
        if (isMounted()) {
          setCopied(false)
          setFadeOut(false)
        }
      }, 2000)
    }

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(fadeOutTimeoutId)
    }
  }, [copied, isMounted])

  const baseClass = cn('flex items-center', {
    'fs-exclude': isPII,
  })

  const finalClasses = twMerge(baseClass, className)
  const copiedClasses = cn('text-meta text-xs transition-opacity duration-500', fadeOut ? 'opacity-0' : 'opacity-100')
  const finalValueClassnames = twMerge(
    cn('text-text-header typo-primary-base', {
      'typo-primary-base-semibold': Boolean(label && value),
    }),
    valueClassName,
  )
  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className="flex-1 px-2xs pr-lg">
        {label ? <p className="typo-primary-sm-regular text-text-meta mb-1">{label}</p> : null}
        <p className={finalValueClassnames}>{value}</p>
      </div>
      {rightValue ? <p className="text-text-header mr-2xs typo-primary-base-semibold">{rightValue}</p> : null}
      {canCopy ? (
        <>
          {copied ? <span className={copiedClasses}>{copiedMessage}</span> : null}
          <CopyToClipboard text={value} data-testid="field-copy-to-clipboard-button">
            <button
              className="p-2 rounded-lg active:bg-light focus:ring-0 focus:outline-1 focus:outline-gray-200"
              type="button"
              onClick={() => {
                setCopied(true)
              }}
            >
              <Square2StackIcon className="w-6 h-6 text-meta" />
            </button>
          </CopyToClipboard>
        </>
      ) : null}
      {onEdit ? (
        <button
          className="p-2 rounded-lg active:bg-light focus:ring-0 focus:outline-1 focus:outline-gray-200"
          type="button"
          data-testid="field-edit-button"
          onClick={onEdit}
        >
          <PencilIcon className="w-4 h-4 text-text-meta mt-[2px]" />
        </button>
      ) : null}
    </div>
  )
}
